import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSuccessFailure } from '../context/SuccessFailureContext';
import config from '../config';
import { FiChevronRight } from 'react-icons/fi';

const RegisterStepTwo = ({ userId: propUserId }) => {
    // Get all hooks first
    const [role, setRole] = useState('');
    const [errors, setErrors] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || '/dashboard';  // Default to dashboard instead of home
    const { isLoggedIn, accessToken, login, autoLogin } = useAuth();  // Get autoLogin function
    const { showSuccessModal, showFailureModal } = useSuccessFailure();

    // Get user ID from props or localStorage
    const userId = propUserId || localStorage.getItem('tempUserId');

    if (!userId) {
        return <div>Please go back and complete step 1 first.</div>;
    }

    const ROLE_CHOICES = {
        'community_member': 'Community Member',
        'event_organizer': 'Event Organizer',
        'event_participant': 'Event Participant',
        'service_provider': 'Service Provider'
    };

    const handleChange = (e) => {
        const selectedRole = e.target.value;
        if (ROLE_CHOICES[selectedRole]) {
            setRole(selectedRole);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await axios.post(`${config.API_BASE_URL}/users/register-step-two/`,
                { role, user_id: userId },
                { withCredentials: true }
            );
            // console.log('Response from backend:', response); // Log response to check structure

            if (response.status === 200 || response.status === 201) {
                try {
                    // Use autoLogin function to update AuthContext state
                    await autoLogin(response.data.access, response.data.user);
                    
                    // Show success message
                    showSuccessModal('Registration successful! Welcome to EventSawa!');
                    
                    // Redirect to dashboard or original page
                    navigate(from, { replace: true });
                } catch (error) {
                    console.error('Registration success but auto login failed:', error);
                    setErrors({ role: 'Registration successful but login failed. Please try logging in manually.' });
                }
            }
        } catch (error) {
            // console.error('Registration error:', error);
            // console.error('Error response:', error.response?.data);
            setErrors({ role: error.response?.data?.error || 'Failed to complete registration. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <>
            {/* Registration Form Section */}
            <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 py-10">
                <div className="max-w-5xl mx-auto p-8">
                    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden">
                        <div className="p-10 md:p-14">
                            <div className="text-center mb-12">
                                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                                    Complete Your Registration
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300">
                                    Choose your role in the community
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <div className="relative">
                                        <select
                                            className="w-full p-3 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                                            value={role}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select your role</option>
                                            {Object.entries(ROLE_CHOICES).map(([value, label]) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors && (
                                        <div className="mt-2 text-red-500 dark:text-red-400 text-sm">
                                            {errors.role ? errors.role : 'Please select a role'}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-8">
                                    <button
                                        type="submit"
                                        disabled={isLoading || !role}
                                        className="w-full flex items-center justify-center py-4 px-8 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Processing...
                                            </>
                                        ) : (
                                            'Complete Registration'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default RegisterStepTwo;
